import { axios } from '@getgreenline/homi-shared'
import { ReceiptLoyaltyModels } from '@getgreenline/loyalties'
import Endpoints from '../constants/Endpoints'

/** The shared axios instance sets no timeout, and a receipt must never wait on this endpoint. */
const REQUEST_TIMEOUT_MS = 5000

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null

const isRedemption = (value: unknown): value is ReceiptLoyaltyModels.Redemption =>
  isRecord(value) && typeof value.paymentLineId === 'number' && typeof value.points === 'number'

const parseRedemptions = (value: unknown): ReceiptLoyaltyModels.Redemption[] =>
  Array.isArray(value)
    ? value.filter(isRedemption).map(({ paymentLineId, points }) => ({ paymentLineId, points }))
    : []

const parsePoints = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null

/**
 * Only `status` can make a body unreadable (`null`) — every other field is defaulted, so one field
 * this build cannot read never costs the merchant the rows the body does carry.
 */
const parseReceiptLoyalty = (body: unknown): ReceiptLoyaltyModels.ReceiptLoyalty | null => {
  if (!isRecord(body)) {
    return null
  }

  // Unchecked: support metadata that nothing renders or branches on.
  const provider = body.provider as ReceiptLoyaltyModels.LoyaltyProvider
  const redemptions = parseRedemptions(body.redemptions)

  if (body.status === 'none') {
    return { status: 'none' }
  }

  if (body.status === 'pending') {
    return { status: 'pending', provider, redemptions }
  }

  if (body.status === 'resolved') {
    return {
      status: 'resolved',
      provider,
      redemptions,
      pointsEarned: parsePoints(body.pointsEarned) ?? 0,
      pointsBalanceAfterSale: parsePoints(body.pointsBalanceAfterSale),
    }
  }

  return null
}

/** Null means the body is not the contract this build knows, which the caller reports as such. */
export const getReceiptLoyalty = async (
  companyId: number,
  locationId: number,
  paymentId: string,
): Promise<ReceiptLoyaltyModels.ReceiptLoyalty | null> => {
  const { data } = await axios.get(
    Endpoints.GET_RECEIPT_LOYALTY(companyId, locationId, paymentId),
    {
      timeout: REQUEST_TIMEOUT_MS,
    },
  )

  return parseReceiptLoyalty(data)
}
